import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Input
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import creditNoteService, {
  CreditNote,
  CreditNoteFilters,
  CreditNoteStats
} from '../../../../services/creditNoteService';
import * as XLSX from 'xlsx';
import apiService from '../../../../services/apiService';
import { useTranslation } from "react-i18next";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface CreditNoteListProps {
  refresh: number;
  onCreate: () => void;
  onView: (cn: CreditNote) => void;
  onEdit: (cn: CreditNote) => void;
  onDuplicate: (cn: CreditNote) => void;
  onPost: (cn: CreditNote) => void;
  onCancel: (cn: CreditNote) => void;
  onDelete: (cn: CreditNote) => void;
}

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  POSTED: 'green',
  CANCELLED: 'red'
};

const fmtMoney = (n: number, currency: string = 'HKD') =>
  `${currency} ${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CreditNoteList: React.FC<CreditNoteListProps> = ({
  refresh,
  onCreate,
  onView,
  onEdit,
  onDuplicate,
  onPost,
  onCancel,
  onDelete
}) => {
    const { t } = useTranslation();
  const [rows, setRows] = useState<CreditNote[]>([]);
  const [stats, setStats] = useState<CreditNoteStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<CreditNoteFilters>({});
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);

  const loadStats = useCallback(async () => {
    try {
      const s = await creditNoteService.getStats();
      setStats(s);
    } catch (err: any) {
      console.error('Failed to load credit-note stats:', err);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await creditNoteService.getAllCreditNotes(filters);
      setRows(data);
    } catch (err: any) {
      message.error(err?.message || t('Failed to load credit notes'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Export the credit notes on screen to Excel, mirroring the invoice export:
   * a summary sheet plus one row per line item.
   *
   * Built from the rows already loaded — the list query includes items,
   * customer and the linked invoice. Deliberately NOT one request per credit
   * note: that pattern is what silently lost data in the invoice export when
   * the rate limit rejected the burst.
   *
   * Account and Department are stored as ids, so the two lookup lists are
   * fetched once (not once per row) to print "410000 - Fulfillment Service
   * Income" instead of a UUID.
   */
  const handleExport = async () => {
    if (rows.length === 0) {
      message.warning(t('No data to export'));
      return;
    }
    const hide = message.loading(t('Building export...'), 0);
    try {
      const [acctResp, deptResp] = await Promise.all([
        apiService.get<any>('/accounting/accounts').catch(() => null),
        apiService.get<any>('/hr/departments').catch(() => null)
      ]);
      const accountById = new Map<string, any>(
        ((acctResp?.data as any[]) || []).map((a: any) => [a.id, a])
      );
      const deptRaw = Array.isArray(deptResp?.data) ? deptResp.data : (deptResp?.data?.departments || []);
      const departmentById = new Map<string, any>((deptRaw as any[]).map((d: any) => [d.id, d]));

      const label = (m: Map<string, any>, id?: string | null) => {
        const x = id ? m.get(id) : null;
        return x ? `${x.code} - ${x.name}` : '';
      };
      const cnDate = (cn: any) => (cn.creditNoteDate ? dayjs(cn.creditNoteDate).format('YYYY-MM-DD') : '');

      const wb = XLSX.utils.book_new();

      // Sheet 1 — one row per credit note
      const summaryRows = rows.map((cn: any) => ({
        'Credit Note No': cn.creditNoteNo || '',
        'Customer': cn.customer?.name || '',
        'Credit Note Date': cnDate(cn),
        'Status': (cn.status || '').toUpperCase(),
        'Currency': cn.currency || '',
        'Total Amount': Number(cn.totalAmount || 0),
        'Linked Invoice': cn.invoice?.invoiceNo || '',
        'Reason': cn.reason || '',
      }));
      const ws1 = XLSX.utils.json_to_sheet(summaryRows);
      ws1['!cols'] = [
        { wch: 20 }, { wch: 32 }, { wch: 16 }, { wch: 12 },
        { wch: 10 }, { wch: 14 }, { wch: 20 }, { wch: 34 },
      ];
      XLSX.utils.book_append_sheet(wb, ws1, 'Credit Note Summary');

      // Sheet 2 — one row per line item, each carrying the credit note number,
      // customer and date so the sheet stands on its own.
      const lineRows: Record<string, any>[] = [];
      rows.forEach((cn: any) => {
        (cn.items || []).forEach((item: any) => {
          lineRows.push({
            'Credit Note No': cn.creditNoteNo || '',
            'Customer': cn.customer?.name || '',
            'Credit Note Date': cnDate(cn),
            // Item Code deliberately omitted: the credit note form has no field
            // for it, so it is only ever set when lines are pulled from an
            // invoice with Copy — 0 of 9 lines on production have one. An
            // always-blank column reads as a bug.
            'Description': item.description || '',
            'Subcategory': item.subcategory || '',
            'Account': label(accountById, item.accountId),
            // Typed name first; older lines still resolve through the HR link.
            'Department': item.departmentName || label(departmentById, item.departmentId),
            'Qty': Number(item.quantity || 0),
            'Unit': item.unit || '',
            'Unit Price': Number(item.unitPrice || 0),
            'Discount': item.discount ? `${item.discount}${item.discountType === 'percentage' ? '%' : ''}` : '',
            'Tax Rate': item.taxRate ? `${item.taxRate}%` : '',
            'Line Total': Number(item.lineTotal || 0),
          });
        });
      });
      const ws2 = XLSX.utils.json_to_sheet(
        lineRows.length > 0 ? lineRows : [{ 'Note': 'No line items found' }]
      );
      ws2['!cols'] = [
        { wch: 20 }, { wch: 32 }, { wch: 16 }, { wch: 34 },
        { wch: 16 }, { wch: 30 }, { wch: 18 }, { wch: 10 }, { wch: 10 },
        { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 14 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, 'Line Items');

      XLSX.writeFile(wb, `Credit_Notes_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      hide();
      message.success(t('Exported {{count}} credit notes with {{lines}} line items', {
        count: rows.length, lines: lineRows.length
      }));
    } catch (error: any) {
      hide();
      message.error(t('Export failed: {{message}}', { message: error?.message || error }));
    }
  };

  const loadCustomers = useCallback(async () => {
    try {
      const resp = await apiService.get<any>('/customers?limit=1000');
      const list = resp?.data?.customers || resp?.customers || resp?.data || [];
      setCustomers(list.map((c: any) => ({ id: c.id, name: c.name })));
    } catch {
      // ignore — filter dropdown remains empty
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadRows();
    loadStats();
  }, [refresh, loadRows, loadStats]);

  const onSearchSubmit = () => {
    setFilters(f => ({ ...f, search: search.trim() || undefined }));
  };

  const onClearFilters = () => {
    setFilters({});
    setSearch('');
  };

  const columns: ColumnsType<CreditNote> = [
    {
      title: t('Credit Note #'),
      dataIndex: 'creditNoteNo',
      key: 'creditNoteNo',
      width: 160,
      render: (text: string, record) => (
        <a onClick={() => onView(record)} style={{ fontWeight: 500 }}>
          {text}
        </a>
      )
    },
    {
      title: t('Customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      ellipsis: true,
      render: (_: any, record) => record.customer?.name || '—'
    },
    {
      title: t('Linked Invoice'),
      dataIndex: ['invoice', 'invoiceNo'],
      key: 'invoice',
      width: 160,
      render: (_: any, record) =>
        record.invoice?.invoiceNo
          ? <span style={{ color: '#1677ff' }}>{record.invoice.invoiceNo}</span>
          : <span style={{ color: '#999' }}>—</span>
    },
    {
      title: t('Date'),
      dataIndex: 'creditNoteDate',
      key: 'creditNoteDate',
      width: 110,
      render: (d: string) => dayjs(d).format('YYYY-MM-DD')
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>
    },
    {
      title: t('Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 160,
      align: 'right',
      render: (v: number, record) => fmtMoney(Number(v || 0), record.currency)
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 420,
      fixed: 'right',
      render: (_: any, record) => {
        const onDownloadPdf = async () => {
          try {
            const { blob, filename } = await creditNoteService.downloadPdf(record.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `${record.creditNoteNo.replace(/\//g, '-')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } catch (err: any) {
            message.error(err?.message || t('Failed to download PDF'));
          }
        };

        // Editing a POSTED credit note is allowed and triggers a JE re-post on
        // the backend. "Settled" CNs (linked to a PAID invoice — the AR has
        // already been reconciled) stay read-only so the closed-out balance
        // can't be changed retroactively.
        const isSettled = record.status === 'POSTED' && record.invoice?.status === 'PAID';
        const canEdit =
          record.status === 'DRAFT' ||
          (record.status === 'POSTED' && !isSettled);

        return (
          <Space size="small" wrap>
            <Button size="small" icon={<EyeOutlined />} onClick={() => onView(record)}>
              {t('View')}
                                </Button>
            {canEdit && (
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(record)}>
                {t('Edit')}
                                      </Button>
            )}
            {record.status === 'DRAFT' && (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => onPost(record)}
              >
                {t('Post')}
                                      </Button>
            )}
            <Button size="small" icon={<CopyOutlined />} onClick={() => onDuplicate(record)}>
              {t('Duplicate')}
                                </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={onDownloadPdf}>
              PDF
            </Button>
            {record.status === 'POSTED' && (
              <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => onCancel(record)}>
                {t('Cancel')}
                                      </Button>
            )}
            {/* Delete used to appear only on drafts. The live system has none
                — every credit note is posted or cancelled — so the button was
                never reachable, which is what "沒有delete" referred to.
                A cancelled credit note has already had its reversing entry
                written, so removing the document leaves the ledger square.
                A posted one is not offered here: it must be cancelled first,
                or the receivable stays reduced with nothing explaining it. */}
            {(record.status === 'DRAFT' || record.status === 'CANCELLED') && (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)}>
                {t('Delete')}
                                      </Button>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={t('Total Credit Notes')}
              value={stats?.totalCreditNotes ?? 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title={t('Draft')} value={stats?.draftCount ?? 0} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={t('Posted')}
              value={stats?.postedCount ?? 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={t('Cancelled')}
              value={stats?.cancelledCount ?? 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input
              placeholder={t('Search CN# / customer / linked invoice')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onPressEnter={onSearchSubmit}
              allowClear
              onClear={() => { setSearch(''); setFilters(f => ({ ...f, search: undefined })); }}
              style={{ width: 280 }}
            />
            <Select
              placeholder={t('Customer')}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.customerId}
              onChange={v => setFilters(f => ({ ...f, customerId: v }))}
              style={{ width: 200 }}
            >
              {customers.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
            <Select
              placeholder={t('Status')}
              allowClear
              value={filters.status}
              onChange={v => setFilters(f => ({ ...f, status: v }))}
              style={{ width: 140 }}
            >
              <Option value="DRAFT">{t('Draft')}</Option>
              <Option value="POSTED">{t('Posted')}</Option>
              <Option value="CANCELLED">{t('Cancelled')}</Option>
            </Select>
            <RangePicker
              value={
                filters.dateFrom && filters.dateTo
                  ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
                  : null
              }
              onChange={(range) => {
                if (!range) {
                  setFilters(f => ({ ...f, dateFrom: undefined, dateTo: undefined }));
                } else {
                  setFilters(f => ({
                    ...f,
                    dateFrom: range[0]?.format('YYYY-MM-DD'),
                    dateTo: range[1]?.format('YYYY-MM-DD')
                  }));
                }
              }}
            />
            <Button onClick={onClearFilters}>{t('Clear')}</Button>
          </Space>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              {t('Export')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
              {t('New Credit Note')}
            </Button>
          </Space>
        </Space>

        <Table<CreditNote>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
        />
      </Card>
    </div>
  );
};

export default CreditNoteList;
